import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export type SalesOpsPeriodType = 'today' | '30d' | 'quarter' | 'custom';

export interface SalesOpsPeriod {
  type: SalesOpsPeriodType;
  dateFrom?: string;
  dateTo?: string;
}

export interface SalesOpsAlert {
  overdueCount: number;
  overdueWithReminderCount: number;
  minDaysOverdue: number;
}

export interface SalesOpsReceiptKpis {
  total: number;
  paid: number;
  pending: number;
  overdue: number;
  paidPercent: number;
  pendingPercent: number;
  overduePercent: number;
}

export interface SalesOpsSalesKpis {
  total: number;
  subscriptionCount: number;
  manualCount: number;
  invoiced: number;
  pendingInvoice: number;
  invoicedPercent: number;
}

export interface SalesOpsWeeklyBucket {
  weekStart: string;
  label: string;
  salesCount: number;
  invoicedCount: number;
}

export interface SalesOpsOverdueRow {
  chargeId?: number;
  saleId?: number;
  chargeLabel: string;
  total: number;
  customerName: string;
  sourceLabel: string;
  daysOverdue: number;
  reminderLabel: string;
  statusLabel: string;
  severity: 'overdue' | 'due_soon';
}

export interface SalesOpsProcessItem {
  key: string;
  title: string;
  description: string;
  tone: 'healthy' | 'warning' | 'neutral';
}

export interface SalesOperationsData {
  from: string;
  to: string;
  updatedAt?: string;
  alert: SalesOpsAlert;
  receipts: SalesOpsReceiptKpis;
  sales: SalesOpsSalesKpis;
  salesTrend: SalesOpsWeeklyBucket[];
  recentOverdue: SalesOpsOverdueRow[];
  processes: SalesOpsProcessItem[];
  loading: boolean;
  error?: string;
}

interface WrapperSalesOperations {
  ok?: boolean;
  message?: string;
  body?: Partial<SalesOperationsData> | null;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function toDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function getSalesOpsRange(period: SalesOpsPeriod): { from: Date; to: Date } {
  const today = startOfDay(new Date());
  if (period.type === 'custom' && period.dateFrom && period.dateTo) {
    return {
      from: startOfDay(new Date(period.dateFrom + 'T00:00:00')),
      to: endOfDay(new Date(period.dateTo + 'T00:00:00'))
    };
  }
  if (period.type === 'today') {
    return { from: today, to: endOfDay(today) };
  }
  if (period.type === 'quarter') {
    const month = today.getMonth();
    const quarterStartMonth = Math.floor(month / 3) * 3;
    return {
      from: startOfDay(new Date(today.getFullYear(), quarterStartMonth, 1)),
      to: endOfDay(today)
    };
  }
  // 30d default
  const from = new Date(today);
  from.setDate(from.getDate() - 29);
  return { from: startOfDay(from), to: endOfDay(today) };
}

@Injectable({ providedIn: 'root' })
export class ForecastSalesOperationsService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  load(period: SalesOpsPeriod = { type: '30d' }): Observable<SalesOperationsData> {
    const { from, to } = getSalesOpsRange(period);
    const params = new HttpParams()
      .set('from', toDayKey(from))
      .set('to', toDayKey(to));

    return this.http
      .get<WrapperSalesOperations>(`${this.apiUrl}/forecasts/sales-operations`, { params })
      .pipe(
        map((res) => {
          if (!res?.ok || !res.body) {
            return this.empty(from, to, res?.message || 'No se pudo cargar el panorama operativo');
          }
          return this.mapApi(res.body, from, to);
        }),
        catchError((err) =>
          of(this.empty(from, to, err?.error?.message || err?.message || 'Error al cargar el panorama operativo'))
        )
      );
  }

  private mapApi(body: Partial<SalesOperationsData>, from: Date, to: Date): SalesOperationsData {
    const r = (body.receipts ?? {}) as Partial<SalesOpsReceiptKpis>;
    const s = (body.sales ?? {}) as Partial<SalesOpsSalesKpis>;
    const alert = (body.alert ?? {}) as Partial<SalesOpsAlert>;
    return {
      from: body.from ?? toDayKey(from),
      to: body.to ?? toDayKey(to),
      updatedAt: body.updatedAt,
      alert: {
        overdueCount: num(alert.overdueCount),
        overdueWithReminderCount: num(alert.overdueWithReminderCount),
        minDaysOverdue: num(alert.minDaysOverdue)
      },
      receipts: {
        total: num(r.total),
        paid: num(r.paid),
        pending: num(r.pending),
        overdue: num(r.overdue),
        paidPercent: num(r.paidPercent),
        pendingPercent: num(r.pendingPercent),
        overduePercent: num(r.overduePercent)
      },
      sales: {
        total: num(s.total),
        subscriptionCount: num(s.subscriptionCount),
        manualCount: num(s.manualCount),
        invoiced: num(s.invoiced),
        pendingInvoice: num(s.pendingInvoice),
        invoicedPercent: num(s.invoicedPercent)
      },
      salesTrend: (body.salesTrend ?? []).map((w) => ({
        weekStart: w.weekStart ?? '',
        label: w.label ?? '',
        salesCount: num(w.salesCount),
        invoicedCount: num(w.invoicedCount)
      })),
      recentOverdue: (body.recentOverdue ?? []).map((row) => ({
        chargeId: row.chargeId,
        saleId: row.saleId,
        chargeLabel: row.chargeLabel ?? '—',
        total: num(row.total),
        customerName: row.customerName ?? '—',
        sourceLabel: row.sourceLabel ?? '—',
        daysOverdue: num(row.daysOverdue),
        reminderLabel: row.reminderLabel ?? '—',
        statusLabel: row.statusLabel ?? '—',
        severity: row.severity === 'due_soon' ? 'due_soon' : 'overdue'
      })),
      processes: (body.processes ?? []).map((p) => ({
        key: p.key ?? '',
        title: p.title ?? '',
        description: p.description ?? '',
        tone: (p.tone as SalesOpsProcessItem['tone']) ?? 'neutral'
      })),
      loading: false
    };
  }

  private empty(from: Date, to: Date, error?: string): SalesOperationsData {
    return {
      from: toDayKey(from),
      to: toDayKey(to),
      alert: { overdueCount: 0, overdueWithReminderCount: 0, minDaysOverdue: 0 },
      receipts: {
        total: 0, paid: 0, pending: 0, overdue: 0,
        paidPercent: 0, pendingPercent: 0, overduePercent: 0
      },
      sales: {
        total: 0, subscriptionCount: 0, manualCount: 0,
        invoiced: 0, pendingInvoice: 0, invoicedPercent: 0
      },
      salesTrend: [],
      recentOverdue: [],
      processes: [],
      loading: false,
      error
    };
  }
}
